import React from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps, useNavigate } from 'react-router-dom';
import { enhanceLinkForWalletBrowser, safeNavigate, isMobile, isWalletBrowser } from '@/utils/mobile-compatibility';
import { cn } from '@/lib/utils';

export interface SafeLinkProps extends RouterLinkProps {
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * SafeLink is a wrapper around React Router's Link component that adds
 * special handling for mobile wallet browsers to prevent blank screens
 * during navigation.
 */
export function SafeLink({
  to,
  className,
  activeClassName,
  inactiveClassName,
  onClick,
  children,
  ...props
}: SafeLinkProps) {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If it's a mobile wallet browser, we need special handling
    if (isMobile && isWalletBrowser()) {
      e.preventDefault();
      
      // Log the navigation attempt
      console.log('SafeLink navigating to:', to);
      
      // Use our safe navigation method
      safeNavigate(navigate, typeof to === 'string' ? to : to.toString());
      
      // Call the original onClick if provided
      if (onClick) onClick(e);
    } else if (onClick) {
      // For regular browsers, just call the onClick handler
      onClick(e);
    }
  };

  return (
    <RouterLink
      to={to}
      className={className}
      onClick={isMobile && isWalletBrowser() ? handleClick : enhanceLinkForWalletBrowser(onClick)}
      {...props}
    >
      {children}
    </RouterLink>
  );
}

/**
 * NavLink is a wrapper around SafeLink that adds active state styling
 */
export function NavLink({
  to,
  className,
  activeClassName = 'active',
  inactiveClassName = '',
  ...props
}: SafeLinkProps) {
  const navigate = useNavigate();
  const isActive = typeof to === 'string' && navigate.toString().includes(to.toString());
  
  return (
    <SafeLink
      to={to}
      className={cn(
        className,
        isActive ? activeClassName : inactiveClassName
      )}
      {...props}
    />
  );
}
